import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Table, Spin, message, DatePicker } from "antd";
import dayjs from "dayjs";
import { useDasTransactions } from "../../hooks/products/das/useDasTransactions";
import { useDasContributionsStatement } from "../../hooks/products/das/useDasContributionsStatement";

// --- START: CustomPagination Component Definition (Required for UI) ---
function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.max(Math.ceil(total / pageSize), 1);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: "transparent",
    color: "#184b18",
    border: "none",
    borderRadius: 0,
    padding: "0 10px",
    minWidth: 0,
    height: 26,
    fontWeight: 700,
    fontSize: 14,
    outline: "none",
    cursor: "pointer",
    transition: "color 0.2s",
    boxShadow: "none",
    margin: "0 2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 1,
  };
  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: "#bdbdbd",
    cursor: "not-allowed",
    opacity: 0.6,
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Previous page"
      >
        {"<"}
      </button>
      {pages.map(page => (
        <button
          key={page}
          onClick={() => onChange(page)}
          style={{
            border: "0.5px solid #184b18",
            background: page === current ? "linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)" : "transparent",
            color: page === current ? "#fff" : "#184b18",
            borderRadius: 18,
            padding: "0 14px",
            minWidth: 36,
            height: 36,
            fontWeight: 700,
            fontSize: 12,
            outline: "none",
            cursor: "pointer",
            transition: "background 0.2s, color 0.2s",
            boxShadow: "none",
            margin: "0 2px",
          }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => current < pageCount && onChange(current + 1)}
        disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Next page"
      >
        {">"}
      </button>
    </div>
  );
}
// --- END: CustomPagination Component Definition ---

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Only values hinted by the API docs (Postman collection); backend should confirm the full set
const DEFAULT_TRANSACTION_TYPES = [
  "Contribution",
  "Redemption",
];

export default function DasTransactionsHistory({ transactions: propTransactions = [] }) {
  const location = useLocation();
  const policyNumber = location.state?.productDetails?.policyNumber;

  const [filterType, setFilterType] = useState("");
  // Default to current year (Jan 1 - Dec 31) so the API always receives date_from/date_to
  const [customDateRange, setCustomDateRange] = useState([dayjs().startOf("year"), dayjs().endOf("year")]);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (policyNumber) setFiltersReady(true);
  }, [policyNumber]);

  const dateRange = Array.isArray(customDateRange) && customDateRange[0] && customDateRange[1]
    ? customDateRange
    : [dayjs().startOf("year"), dayjs().endOf("year")];

  const filters = {
    policyNumber,
    transaction_type: filterType || "",
    date_from: dateRange[0].format("YYYY-MM-DD"),
    date_to: dateRange[1].format("YYYY-MM-DD"),
  };

  const { data: transactionsData, isLoading: transactionsLoading } = useDasTransactions(
    filters,
    !!policyNumber && filtersReady
  );

  // Contributions statement download (server-side PDF)
  const { mutate: downloadContributionsStatement, isPending: isDownloadingStatement } =
    useDasContributionsStatement();

  const handleContributionsStatementDownload = () => {
    if (!policyNumber) {
      message.error("Policy number is missing.");
      return;
    }
    const year = new Date().getFullYear();
    downloadContributionsStatement(
      { policyNumber, startYear: year, endYear: year },
      {
        onSuccess: (res) => {
          const url = res?.data?.data || res?.data;
          if (url) {
            window.open(url, "_blank");
            message.success("Contributions statement is being downloaded.");
          } else {
            message.error("No statement URL returned. Please try again.");
          }
        },
        onError: (err) => {
          message.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to download contributions statement. Please try again."
          );
        },
      }
    );
  };

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef(null);

  // Normalize transactions for table
  const extractTransactions = (payload) => {
    if (!payload) return propTransactions;
    const raw = payload?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.transactions)) return raw.transactions;
    if (Array.isArray(raw?.transaction_history?.transactions)) return raw.transaction_history.transactions;
    if (Array.isArray(payload?.transactions)) return payload.transactions;
    if (Array.isArray(payload)) return payload;
    return propTransactions;
  };

  const rawTransactions = transactionsData ? extractTransactions(transactionsData) : propTransactions;

  const normalizedTransactions = rawTransactions.map((item) => {
    const src = item || {};
    return {
      date: src.transaction_date || src.payment_date || src.created_at || src.date || src.transactionDate || "",
      type: src.transaction_type || src.type || src.description || src.transactionType || "",
      reference: src.reference || src.transaction_ref || src.ref || src.reference_no || "",
      narration: src.narration || src.description || src.remarks || "",
      amount: src.amount ?? src.received ?? src.contribution_amount ?? src.debit ?? src.credit ?? 0,
      balance: src.balance ?? src.running_balance ?? src.account_balance ?? null,
    };
  });

  // Sort by date descending
  const sortedTransactions = [...normalizedTransactions].sort((a, b) => {
    const ta = new Date(a.date).getTime() || 0;
    const tb = new Date(b.date).getTime() || 0;
    return tb - ta;
  });

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      render: date => <div style={{ textAlign: "left", paddingLeft: 8, whiteSpace: "nowrap" }}>{formatDate(date)}</div>
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "left"
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      align: "left",
      responsive: ["md"]
    },
    {
      title: "Narration",
      dataIndex: "narration",
      key: "narration",
      align: "left"
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: amount => <span style={{ color: "#111", fontWeight: 600, whiteSpace: "nowrap" }}>GHS {Math.abs(Number(amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "left",
      render: balance => balance !== null && balance !== undefined
        ? <span style={{ whiteSpace: "nowrap" }}>GHS {Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        : "-",
      responsive: ["sm"]
    },
  ];

  let filteredTransactions = sortedTransactions;
  const isSearchActive = !!searchValue;
  if (isSearchActive) {
    const val = searchValue.trim().toLowerCase();
    filteredTransactions = filteredTransactions.filter(t =>
      (t.type || "").toLowerCase().includes(val) ||
      (t.reference || "").toLowerCase().includes(val) ||
      (t.narration || "").toLowerCase().includes(val) ||
      (t.amount || 0).toString().includes(val)
    );
  }

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const paginatedTransactions = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(startIdx, startIdx + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  useEffect(() => { setCurrentPage(1); }, [filterType, customDateRange, searchValue]);

  function handleSearchClick() {
    setShowSearch(prev => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  }

  return (
    <div>
      <div className="container p-0 transactions-page">
        <div className="py-4 d-flex flex-column align-items-center">
          {/* HEADER/FILTERS ROW */}
          <div className="mb-3 w-100" style={{ maxWidth: 1330, paddingLeft: 12, paddingRight: 12 }}>
            <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 16 }}>
              <div className="mb-2 mb-sm-0">
                <div className="page-title">DAS Transactions</div>
              </div>

              <div className="d-flex flex-wrap align-items-center controls-container" style={{ gap: 12 }}>
                {/* Search Input */}
                <div className="search-container" style={{ position: "relative", display: "flex", alignItems: "center", borderRadius: 24, background: "#fff", boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)", padding: "4px 4px 4px 12px", transition: "all .28s ease", width: showSearch ? 280 : 120, minWidth: 120 }}>
                  <i
                    className="bi bi-search"
                    style={{ fontSize: 18, color: "#666", cursor: "pointer", zIndex: 2, marginRight: 8, flexShrink: 0 }}
                    aria-label="Search"
                    onClick={handleSearchClick}
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") handleSearchClick(); }}
                    role="button"
                  ></i>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={showSearch ? "Search by type/ref/narration..." : "Search"}
                    className="form-control border-0"
                    style={{ background: "transparent", border: "none", boxShadow: "none", textAlign: "left", padding: "8px 0", width: showSearch ? 200 : 60, opacity: 1, transition: "width .28s ease" }}
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setShowSearch(false); }}
                  />
                </div>

                {/* Transaction Type Filter */}
                <div className="controls-left d-flex align-items-center" style={{ gap: 12 }}>
                  <omk-select
                    label="Transaction Type"
                    value={filterType}
                    disabled={!policyNumber}
                    onInput={(e) => setFilterType(e.target.value)}
                  >
                    <omk-select-option value="">All types</omk-select-option>
                    {DEFAULT_TRANSACTION_TYPES.map(t => (
                      <omk-select-option key={t} value={t}>{t}</omk-select-option>
                    ))}
                  </omk-select>

                  <DatePicker.RangePicker
                    value={Array.isArray(customDateRange) ? customDateRange : [null, null]}
                    onChange={vals => {
                      if (!vals || !vals[0] || !vals[1]) {
                        // Clear falls back to the current year so API params stay populated
                        setCustomDateRange([dayjs().startOf("year"), dayjs().endOf("year")]);
                      } else {
                        setCustomDateRange(vals);
                      }
                    }}
                    allowClear
                    format="YYYY-MM-DD"
                    disabled={!policyNumber}
                    className="das-date-range"
                    popupClassName="das-date-picker-dropdown"
                    style={{ minWidth: 220, maxWidth: "100%", borderRadius: 24, height: 40 }}
                  />
                </div>

                {/* Download Contribution Statement Button */}
                <div className="controls-right d-flex align-items-center">
                  <button className="btn btn-primary generate-btn" onClick={handleContributionsStatementDownload} disabled={!policyNumber || isDownloadingStatement}>
                    {isDownloadingStatement
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Spin size="small" /> Preparing...</span>
                      : "Download Statement"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Component-level responsive styles */}
          <style>{`
            .transactions-page .page-title { font-weight:700; color:#111; font-size: clamp(18px, 2.2vw, 22px); }
            .transactions-page .search-container { border-radius: 24px; background: #fff; box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08); overflow: hidden; }
            .transactions-page .generate-btn { border-radius: 24px; height: 36px; font-weight: 600; min-width: 120px; }
            .transactions-page .card { width: 97%; max-width: 1330px; min-width: 0; }
            .transactions-page .ant-table-wrapper { overflow-x: auto; }
            .transactions-page .ant-table-body { overflow-x: auto !important; }
            .transactions-page .ant-table tbody td { font-size: clamp(12px, 1.8vw, 14px); }
            .transactions-page .ant-table thead th { font-size: clamp(12px, 1.8vw, 13px); }
            .transactions-page .ant-picker { width: auto; min-width: 220px; }

            /* Green theme for the DAS date range picker (input + dropdown panel) */
            .transactions-page .ant-picker.das-date-range {
              border-color: #00b050 !important;
            }
            .transactions-page .ant-picker.das-date-range:hover,
            .transactions-page .ant-picker.das-date-range.ant-picker-focused {
              border-color: #00b050 !important;
              box-shadow: 0 0 0 2px rgba(0, 176, 80, 0.15) !important;
            }
            .transactions-page .ant-picker.das-date-range .ant-picker-active-bar {
              background: #00b050 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown {
              z-index: 99999 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-inner {
              border-radius: 8px !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-in-range::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-start::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-end::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-hover-start::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-hover-end::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-hover::before,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-hover-start::after,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-hover-end::after,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-in-range::after {
              background: rgba(0, 176, 80, 0.12) !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-start .ant-picker-cell-inner,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-range-end .ant-picker-cell-inner,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-selected .ant-picker-cell-inner {
              background: #00b050 !important;
              color: #fff !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-today .ant-picker-cell-inner::before {
              border-color: #00b050 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell:hover .ant-picker-cell-inner {
              background: rgba(0, 176, 80, 0.12) !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-header-view button:hover {
              color: #00b050 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-today-btn,
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-now-btn {
              color: #00b050 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-content th {
              color: #184b18 !important;
              font-weight: 600 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-cell-disabled .ant-picker-cell-inner {
              color: #d9d9d9 !important;
            }
            .ant-picker-dropdown.das-date-picker-dropdown .ant-picker-suffix {
              color: #00b050 !important;
            }

            @media (max-width: 900px) {
              .transactions-page .generate-btn { min-width: 96px; padding: 8px 12px; }
              .transactions-page .search-container { width: 160px !important; min-width: 160px !important; }
            }

            @media (max-width: 576px) {
              .transactions-page { padding: 8px; }
              .transactions-page .page-title { font-size: 16px; margin-bottom: 12px; }
              .transactions-page .controls-container { width: 100%; flex-direction: column; align-items: stretch; gap: 12px; }
              .transactions-page .controls-left { width: 100%; flex-direction: column; align-items: stretch; gap: 12px; }
              .transactions-page .controls-left > div { width: 100%; }
              .transactions-page .controls-left omk-select { width: 100% !important; min-width: 100% !important; }
              .transactions-page .controls-left .ant-picker { width: 100% !important; }
              .transactions-page .controls-right { width: 100%; }
              .transactions-page .controls-right .generate-btn { width: 100%; }
              .transactions-page .search-container { width: 100% !important; min-width: 100% !important; padding: 8px 12px 8px 12px !important; }
              .transactions-page .search-container input { width: 100% !important; }
              .transactions-page .generate-btn { min-width: unset; padding: 10px 16px; font-size: 13px; height: 40px; }
              .transactions-page .card { width: 100%; min-width: 0; border-radius: 10px; }
              .transactions-page .ant-table { font-size: 11px; }
              .transactions-page .ant-table table { min-width: max-content; }
              .transactions-page .ant-table thead th, .transactions-page .ant-table tbody td { padding: 8px 4px; font-size: 11px; }
              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 { flex-direction: column; gap: 12px; padding: 12px; }
            }

            @media (max-width: 480px) {
              .transactions-page .page-title { font-size: 15px; }
              .transactions-page .search-container { padding: 6px 10px 6px 10px !important; }
              .transactions-page .ant-table { font-size: 10px; }
              .transactions-page .ant-table thead th, .transactions-page .ant-table tbody td { padding: 6px 2px; font-size: 10px; }
            }
          `}</style>

          {/* Table Container */}
          <div className="w-100 d-flex justify-content-center" style={{ minHeight: 120, background: "transparent" }}>
            <div className="card p-0 shadow-sm" style={{ width: "97%", maxWidth: 1330, minWidth: 320, color: "#111", overflow: "auto", borderRadius: 10, position: "relative" }}>
              {transactionsLoading && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 20 }}>
                  <Spin size="large" />
                </div>
              )}
              <Table
                columns={columns}
                dataSource={paginatedTransactions}
                rowKey={(record, idx) => idx}
                pagination={false}
                locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No transactions found.</span> }}
                style={{ overflow: "hidden", borderRadius: 24 }}
                scroll={{ x: "max-content" }}
              />
            </div>
          </div>

          {/* Pagination */}
          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ maxWidth: 1330, minWidth: 0, width: "100%", borderRadius: 10, border: "none", background: "transparent" }}>
              <div className="text-muted d-none d-sm-block" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{paginatedTransactions.length} of {filteredTransactions.length} results
              </div>
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredTransactions.length}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
